/**
 * OrbitalDebrisSystem — tracks orbiting debris count for the Dense path.
 *
 * Orbital Debris (dense_path_passive) and Slingshot (slingshot) share a
 * debris count. While Collapse is active the system accumulates debris
 * over time up to a maximum of 5 objects. Slingshot consumes all debris
 * for its burst attack.
 *
 * Design values (from design-decisions.md § Astral Base Values):
 * - Time to fill orbit: 10 000ms (10 seconds) at base rate.
 * - Maximum debris objects: 5.
 *
 * The accumulation rate is multiplier-adjustable (Rapid Orbit upgrade sets
 * `orbitAccumulationMultiplier` via the resolver; the passive effect calls
 * setAccumulationMultiplier() each frame or on unlock).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § OrbitalDebrisSystem
 * @see design-decisions.md § Astral Base Values
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum number of orbiting debris objects. */
const MAX_DEBRIS_COUNT = 5;
/** Base fill time for a full orbit in ms (10 seconds). */
const BASE_FILL_TIME_MS = 10_000;
/** Base accumulation rate: debris per ms. */
const BASE_RATE_PER_MS = MAX_DEBRIS_COUNT / BASE_FILL_TIME_MS;

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class OrbitalDebrisSystem {
  /** Fractional accumulator — drives integer count. */
  private _accumulator = 0;
  /** Whether the system is currently accumulating (Collapse active). */
  private _active = false;
  /** Multiplier applied to base accumulation rate. Set by Rapid Orbit upgrade. */
  private _accumulationMultiplier = 1.0;

  // ---------------------------------------------------------------------------
  // State accessors
  // ---------------------------------------------------------------------------

  /** Current integer count of orbiting debris objects. */
  get count(): number {
    return Math.min(MAX_DEBRIS_COUNT, Math.floor(this._accumulator));
  }

  /** Maximum debris objects allowed in orbit. */
  get maxCount(): number {
    return MAX_DEBRIS_COUNT;
  }

  /** Whether the system is currently accumulating debris. */
  get isActive(): boolean {
    return this._active;
  }

  // ---------------------------------------------------------------------------
  // Control
  // ---------------------------------------------------------------------------

  /**
   * Begin accumulating debris. Called when Collapse activates.
   * Does not reset existing count — allows resuming a partial orbit.
   */
  startAccumulating(): void {
    this._active = true;
  }

  /**
   * Stop accumulating debris. Does not clear the orbit.
   * Call clear() to also remove the debris.
   */
  stopAccumulating(): void {
    this._active = false;
  }

  /**
   * Set the orbit accumulation rate multiplier.
   * Pass the resolver's multiplicative value for 'orbitAccumulationMultiplier'.
   * Default is 1.0 (base rate, 10s fill time).
   */
  setAccumulationMultiplier(multiplier: number): void {
    this._accumulationMultiplier = Math.max(0.1, multiplier);
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Per-frame accumulation tick. Only accumulates while active and below cap.
   * @param delta Frame delta in ms.
   */
  update(delta: number): void {
    if (!this._active) return;
    if (this._accumulator >= MAX_DEBRIS_COUNT) return;

    this._accumulator = Math.min(
      MAX_DEBRIS_COUNT,
      this._accumulator + BASE_RATE_PER_MS * this._accumulationMultiplier * delta,
    );
  }

  /**
   * Consume all debris for a Slingshot burst.
   * @returns The integer count consumed (0 if orbit is empty).
   */
  consumeAll(): number {
    const consumed = this.count;
    this._accumulator = 0;
    return consumed;
  }

  /**
   * Clear orbit completely. Called when Collapse deactivates or expires.
   * Also stops accumulation.
   */
  clear(): void {
    this._accumulator = 0;
    this._active = false;
  }

  /** Release all state. */
  destroy(): void {
    this.clear();
    this._accumulationMultiplier = 1.0;
  }
}
